/**
 * Ads: what Amazon's console cannot show, because it does not know purchase cost, stock or total sales.
 *
 *   Top row       spend, ad sales, ACOS, TACOS (spend over the same platform's sales), profit after ads,
 *                 wasted spend. ACOS/TACOS for everyone; profit, break-even and verdicts for admins.
 *   Wasted spend  rupee-valued lists: ads on SKUs without stock, loss-making after ads (admins), spend with
 *                 no ad sales, spend not linked to a SKU.
 *   Tables        by brand / category / platform (share of spend vs share of sales), by SKU (verdict,
 *                 break-even ACOS, days of stock, 14-day spend sparkline), CSV export.
 *   Trend         weekly spend, ad sales, platform sales and TACOS; the last days are "still settling".
 *   Settings tab  (admins) verdict thresholds and GST treatment of ad spend.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, Download, Info, RefreshCw, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { KpiCard } from '../components/sales/KpiCard';
import { Sparkline } from '../components/sales/Sparkline';
import { downloadCsv, indiaToday, shiftDay, shortMoney, wholeMoney } from '../components/sales/format';
import { LoadError, errorMessage } from '../components/LoadError';
import { PLATFORMS } from '../config/platforms';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/ads`;
const ALL = 'ALL';

const platformName = code => PLATFORMS[code]?.name || code;
const pct = value => (value == null ? '—' : `${value.toFixed(1)}%`);
const tone = value => (value < 0 ? 'text-red-600' : 'text-gray-900');
const when = value => (value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const dayLabel = value => (value ? new Date(`${value}T12:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—');

const PERIODS = [
  { id: '14', label: 'Last 14 days' },
  { id: '30', label: 'Last 30 days' },
  { id: 'mtd', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: '90', label: 'Last 90 days' },
];

const periodRange = id => {
  const end = shiftDay(indiaToday(), -1);
  if (id === 'mtd') return { date_from: `${end.slice(0, 7)}-01`, date_to: end };
  if (id === 'last_month') {
    const lastDay = shiftDay(`${indiaToday().slice(0, 7)}-01`, -1);
    return { date_from: `${lastDay.slice(0, 7)}-01`, date_to: lastDay };
  }
  return { date_from: shiftDay(end, 1 - Number(id)), date_to: end };
};

// Verdicts: what to do with each SKU's ads (admins only; computed on the server from cost, fees and stock)
const VERDICTS = {
  scale: { label: 'Scale', cls: 'bg-green-100 text-green-800', action: 'Raise budget or bids' },
  hold: { label: 'Hold', cls: 'bg-amber-100 text-amber-800', action: 'Optimise, do not grow' },
  cut: { label: 'Cut', cls: 'bg-red-100 text-red-800', action: 'Lower bids or pause' },
  pause_stock: { label: 'Pause: stock', cls: 'bg-orange-100 text-orange-800', action: 'Pause until stock arrives' },
  fix_price: { label: 'Fix price', cls: 'bg-gray-800 text-white', action: 'Review price or cost first' },
  too_early: { label: 'Too early', cls: 'bg-gray-100 text-gray-700', action: 'Wait for more data' },
  unlinked: { label: 'Not linked', cls: 'bg-blue-100 text-blue-800', action: 'Map in Code Mappings' },
};

const Verdict = ({ row }) => {
  const v = VERDICTS[row.verdict] || VERDICTS.too_early;
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${v.cls}`} title={row.verdict_reason} data-testid={`verdict-${row.sku_code}`}>
      {v.label}
    </span>
  );
};

const Select = ({ value, onChange, children, testId, label }) => (
  <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
    {label}
    <select value={value} onChange={event => onChange(event.target.value)} data-testid={testId}
      className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900">
      {children}
    </select>
  </label>
);

const Notice = ({ tone: kind = 'blue', children, testId }) => {
  const cls = { blue: 'border-blue-200 bg-blue-50 text-blue-900', amber: 'border-amber-200 bg-amber-50 text-amber-900', red: 'border-red-200 bg-red-50 text-red-900' }[kind];
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${cls}`} data-testid={testId}>
      {kind === 'blue' ? <Info className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span>{children}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------- tables

const GroupTable = ({ rows, admin, testId, onPick, picked }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
        <tr>
          <th className="px-3 py-2">Name</th>
          <th className="px-3 py-2 text-right">Ad spend</th>
          <th className="px-3 py-2 text-right">Ad sales</th>
          <th className="px-3 py-2 text-right">ACOS</th>
          <th className="px-3 py-2 text-right" title="Sales on the platforms whose ads are connected">Platform sales</th>
          <th className="px-3 py-2 text-right" title="Ad spend ÷ sales on the same platform">TACOS</th>
          <th className="px-3 py-2 text-right" title="All channels, for context only; never used in a ratio">All-channel sales</th>
          {admin && <th className="px-3 py-2 text-right" title="Contribution per unit ÷ selling price">Break-even ACOS</th>}
          {admin && <th className="px-3 py-2 text-right">Profit after ads</th>}
          <th className="px-3 py-2 text-right">Share of spend</th>
          <th className="px-3 py-2 text-right">Share of sales</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && <tr><td colSpan={admin ? 11 : 9} className="px-3 py-6 text-center text-gray-500">No ad spend in this period.</td></tr>}
        {rows.map(row => (
          <tr key={row.key} className={`${onPick ? 'cursor-pointer hover:bg-gray-50' : ''} ${picked === row.key ? 'bg-blue-50' : ''}`}
            onClick={onPick ? () => onPick(row.key) : undefined} data-testid={`${testId}-${row.key}`}>
            <td className="px-3 py-2 font-medium text-gray-900">
              {row.label}
              {row.vendor_note && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800" title="Amazon Vendor sales here are dispatches to Amazon, not customer sales">dispatch-based</span>}
              {row.ads_connected === false && <span className="ml-2 text-[11px] text-gray-500">ads not connected</span>}
            </td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.spend)}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.ad_sales)}</td>
            <td className="px-3 py-2 text-right">{pct(row.acos)}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.platform_sales)}</td>
            <td className="px-3 py-2 text-right font-semibold">{pct(row.tacos)}</td>
            <td className="px-3 py-2 text-right text-gray-500">{wholeMoney(row.all_channel_sales)}</td>
            {admin && <td className="px-3 py-2 text-right">{pct(row.break_even_acos)}</td>}
            {admin && <td className={`px-3 py-2 text-right font-semibold ${tone(row.net_profit)}`}>{wholeMoney(row.net_profit)}</td>}
            <td className="px-3 py-2 text-right">{pct(row.share_spend)}</td>
            <td className={`px-3 py-2 text-right ${row.share_sales != null && row.share_spend != null && row.share_spend - row.share_sales > 5 ? 'text-red-600' : ''}`}>{pct(row.share_sales)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SkuTable = ({ rows, admin, testId }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
        <tr>
          <th className="px-3 py-2">SKU</th>
          {admin && <th className="px-3 py-2">Verdict</th>}
          <th className="px-3 py-2 text-right">Ad spend</th>
          <th className="px-3 py-2">14 days</th>
          <th className="px-3 py-2 text-right">Ad sales</th>
          <th className="px-3 py-2 text-right">Orders</th>
          <th className="px-3 py-2 text-right">ACOS</th>
          {admin && <th className="px-3 py-2 text-right">Break-even</th>}
          <th className="px-3 py-2 text-right">TACOS</th>
          <th className="px-3 py-2 text-right">CTR</th>
          <th className="px-3 py-2 text-right">CPC</th>
          <th className="px-3 py-2 text-right">Days of stock</th>
          {admin && <th className="px-3 py-2 text-right">Profit after ads</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && <tr><td colSpan={admin ? 13 : 10} className="px-3 py-6 text-center text-gray-500">No SKUs match.</td></tr>}
        {rows.map(row => (
          <tr key={row.sku_code} data-testid={`ads-sku-${row.sku_code}`}>
            <td className="px-3 py-2">
              <div className="font-medium text-gray-900">{row.sku_code}</div>
              <div className="text-xs text-gray-500">{row.product_name} · {row.brand}{row.vendor_note ? ' · dispatch-based' : ''}</div>
            </td>
            {admin && <td className="px-3 py-2"><Verdict row={row} /><div className="mt-0.5 max-w-[220px] text-[11px] leading-tight text-gray-500">{row.verdict_reason}</div></td>}
            <td className="px-3 py-2 text-right">{wholeMoney(row.spend)}</td>
            <td className="px-3 py-2"><Sparkline data={row.sparkline} color="#7C3AED" height={22} fill={false} className="w-[70px]" label={`Daily ad spend, last ${row.sparkline.length} days`} /></td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.ad_sales)}</td>
            <td className="px-3 py-2 text-right">{row.orders.toLocaleString('en-IN')}</td>
            <td className="px-3 py-2 text-right">{pct(row.acos)}</td>
            {admin && <td className="px-3 py-2 text-right">{pct(row.break_even_acos)}</td>}
            <td className="px-3 py-2 text-right font-semibold">{pct(row.tacos)}</td>
            <td className="px-3 py-2 text-right">{pct(row.ctr)}</td>
            <td className="px-3 py-2 text-right">{row.cpc == null ? '—' : `₹${row.cpc.toFixed(2)}`}</td>
            <td className={`px-3 py-2 text-right ${row.days_of_stock != null && row.days_of_stock < 7 ? 'text-red-600 font-semibold' : ''}`}>{row.days_of_stock == null ? '—' : row.days_of_stock >= 999 ? '∞' : row.days_of_stock}</td>
            {admin && <td className={`px-3 py-2 text-right font-semibold ${tone(row.net_profit)}`}>{wholeMoney(row.net_profit)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const skuCsv = (rows, admin) => [
  ['SKU', 'Product', 'Brand', 'Category', ...(admin ? ['Verdict', 'Reason'] : []), 'Ad spend', 'Ad sales (14-day attributed)', 'Orders', 'ACOS %',
    ...(admin ? ['Break-even ACOS %', 'Headroom pts'] : []), 'Platform sales', 'TACOS %', 'Impressions', 'Clicks', 'CTR %', 'CPC', 'Days of stock',
    ...(admin ? ['Profit after ads'] : [])],
  ...rows.map(r => [r.sku_code, r.product_name, r.brand, r.category, ...(admin ? [VERDICTS[r.verdict]?.label || r.verdict, r.verdict_reason] : []),
    r.spend, r.ad_sales, r.orders, r.acos, ...(admin ? [r.break_even_acos, r.headroom] : []), r.platform_sales, r.tacos, r.impressions, r.clicks,
    r.ctr, r.cpc, r.days_of_stock, ...(admin ? [r.net_profit] : [])]),
];

// ---------------------------------------------------------------------------- report

const GROUPS = { brand: { label: 'Brand', key: 'by_brand' }, category: { label: 'Category', key: 'by_category' }, platform: { label: 'Platform', key: 'by_platform' } };

const AdsReport = ({ country, admin }) => {
  const [period, setPeriod] = useState('30');
  const [filters, setFilters] = useState({ platform: ALL, brand: ALL, category: ALL });
  const [group, setGroup] = useState('brand');
  const [verdictFilter, setVerdictFilter] = useState(ALL);
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [version, setVersion] = useState(0);

  const query = useMemo(() => {
    const params = new URLSearchParams({ country, ...periodRange(period) });
    Object.entries(filters).forEach(([key, value]) => { if (value !== ALL) params.set(key, value); });
    return params.toString();
  }, [country, period, filters]);

  useEffect(() => {
    const controller = new AbortController();
    setState(prev => ({ ...prev, loading: true, error: null }));
    axios.get(`${API}/report?${query}`, { signal: controller.signal })
      .then(response => setState({ data: response.data, loading: false, error: null }))
      .catch(error => { if (!controller.signal.aborted) setState(prev => ({ ...prev, loading: false, error })); });
    return () => controller.abort();
  }, [query, version]);

  const { data, loading, error } = state;
  const s = data?.summary;
  const p = data?.previous;
  const setFilter = key => value => setFilters(prev => ({ ...prev, [key]: value }));
  const groupRows = data?.[GROUPS[group].key] || [];
  const skuRows = (data?.by_sku || []).filter(row => verdictFilter === ALL || row.verdict === verdictFilter);
  const verdictCounts = useMemo(() => (data?.by_sku || []).reduce((acc, row) => ({ ...acc, [row.verdict]: (acc[row.verdict] || 0) + 1 }), {}), [data]);
  const pickGroup = key => {
    if (group === 'brand') setFilter('brand')(filters.brand === key ? ALL : key);
    if (group === 'category') setFilter('category')(filters.category === key ? ALL : key);
    if (group === 'platform') setFilter('platform')(filters.platform === key ? ALL : key);
  };
  const pickedGroup = { brand: filters.brand, category: filters.category, platform: filters.platform }[group];
  // Brands with a TACOS value somewhere in the period (spend not linked to a SKU has no sales to compare with)
  const brandsInTrend = useMemo(() => Array.from(new Set((data?.trend || []).flatMap(w => Object.entries(w.brand_tacos || {}).filter(([, v]) => v != null).map(([b]) => b)))).slice(0, 6), [data]);
  const trendRows = useMemo(() => (data?.trend || []).map(w => ({ ...w, label: dayLabel(w.week), ...Object.fromEntries(brandsInTrend.map(b => [`tacos_${b}`, w.brand_tacos?.[b] ?? null])) })), [data, brandsInTrend]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <Select label="Period" value={period} onChange={setPeriod} testId="ads-period">
          {PERIODS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
        </Select>
        <Select label="Platform" value={filters.platform} onChange={setFilter('platform')} testId="ads-platform">
          <option value={ALL}>All ad platforms</option>
          {(data?.options.platforms || []).map(code => <option key={code} value={code}>{platformName(code)}</option>)}
        </Select>
        <Select label="Brand" value={filters.brand} onChange={setFilter('brand')} testId="ads-brand">
          <option value={ALL}>All brands</option>
          {(data?.options.brands || []).map(brand => <option key={brand} value={brand}>{brand}</option>)}
        </Select>
        <Select label="Category" value={filters.category} onChange={setFilter('category')} testId="ads-category">
          <option value={ALL}>All categories</option>
          {(data?.options.categories || []).map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </Select>
        <Button variant="outline" onClick={() => setVersion(v => v + 1)} disabled={loading} data-testid="ads-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        {data && (
          <span className={`ml-auto text-xs ${data.meta.freshness.stale ? 'text-amber-700' : 'text-gray-500'}`} data-testid="ads-freshness">
            Data to {dayLabel(data.meta.freshness.last_ad_date)} · synced {when(data.meta.freshness.last_synced_at)}
            {data.meta.freshness.stale ? ' · older than 36 hours' : ''}
          </span>
        )}
      </div>

      {error && <LoadError what="the ads report" error={error} onRetry={() => setVersion(v => v + 1)} />}

      {data && !data.meta.has_ads && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600" data-testid="ads-empty">
          No ad spend yet. {admin ? <>Add an ads connection in <Link to="/settings/integrations" className="text-blue-700 underline">Settings → Integrations</Link>.</> : 'Ask an admin to connect Amazon Ads.'}
        </div>
      )}

      {data && data.meta.has_ads && (
        <>
          {data.meta.platforms_missing_ads.length > 0 && (
            <Notice testId="ads-missing">
              TACOS compares each platform&apos;s ads with that platform&apos;s own sales. Ads are not connected for {data.meta.platforms_missing_ads.join(', ')}, so no blended figure is shown.
            </Notice>
          )}
          {data.meta.vendor_note && (
            <Notice tone="amber" testId="ads-vendor-note">
              Amazon Vendor figures use dispatches to Amazon, not customer sales, so Vendor TACOS is approximate until Vendor Central sell-through is connected.
            </Notice>
          )}

          <div className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${admin ? 'xl:grid-cols-6' : 'xl:grid-cols-5'} ${loading ? 'opacity-60' : ''}`}>
            <KpiCard label={`Ad spend${data.meta.ads_gst_is_cost ? ' (incl GST)' : ' (excl GST)'}`} value={shortMoney(s.spend)} current={s.spend} previous={p.spend} invert noun="ad spend" compareLabel="vs previous period" testId="ads-kpi-spend" />
            <KpiCard label="Ad sales (attributed by Amazon, 14-day)" value={shortMoney(s.ad_sales)} current={s.ad_sales} previous={p.ad_sales} noun="ad sales" compareLabel="vs previous period" testId="ads-kpi-sales" />
            <KpiCard label="ACOS" value={pct(s.acos)} current={s.acos || 0} previous={p.acos || 0} kind="percent" invert compareLabel="vs previous period" footnote={`CTR ${pct(s.ctr)} · CPC ${s.cpc == null ? '—' : `₹${s.cpc}`}`} testId="ads-kpi-acos" />
            <KpiCard label="TACOS" value={pct(s.tacos)} current={s.tacos || 0} previous={p.tacos || 0} kind="percent" invert compareLabel="vs previous period" footnote={`Platform sales ${shortMoney(s.platform_sales)}`} hint="Ad spend ÷ sales on the same platform" testId="ads-kpi-tacos" />
            {admin && <KpiCard label="Profit after ads" value={shortMoney(s.net_profit)} current={s.net_profit} previous={p.net_profit} compareLabel="vs previous period" footnote={`Contribution before ads ${shortMoney(s.contribution)}`} testId="ads-kpi-net" />}
            <KpiCard label="Wasted spend" value={shortMoney(s.wasted_spend)} footnote={s.spend ? `${pct(s.wasted_spend / s.spend * 100)} of ad spend` : undefined} hint="Spend on SKUs without stock, losing money, with no ad sales, or not linked to a SKU" testId="ads-kpi-waste" />
          </div>

          {data.waste.length > 0 && (
            <div className="space-y-2" data-testid="ads-waste">
              <h2 className="text-sm font-semibold text-gray-900">Wasted spend: money you can save</h2>
              <div className="grid gap-3 md:grid-cols-2">
                {data.waste.map(item => (
                  <div key={item.kind} className="rounded-lg border border-red-200 bg-white p-3" data-testid={`ads-waste-${item.kind}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium text-gray-900">{item.label}</span>
                      <span className="text-lg font-semibold tabular-nums text-red-700">{wholeMoney(item.amount)}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">{item.hint}</p>
                    <p className="mt-1 font-mono text-[11px] text-gray-500">
                      {item.skus.slice(0, 6).map(x => x.sku_code).join(', ')}{item.count > 6 ? ` … +${item.count - 6}` : ''}
                      {item.kind === 'unlinked' && <> · <Link to="/code-mappings" className="font-sans text-blue-700 underline">Code Mappings</Link></>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1" role="tablist">
              {Object.entries(GROUPS).map(([id, item]) => (
                <Button key={id} size="sm" variant={group === id ? 'default' : 'outline'} onClick={() => setGroup(id)} data-testid={`ads-by-${id}`}>By {item.label.toLowerCase()}</Button>
              ))}
              <span className="ml-2 text-xs text-gray-500">Click a row to filter the page to it.</span>
            </div>
            <GroupTable rows={groupRows} admin={admin} testId={`ads-${group}-table`} onPick={pickGroup} picked={pickedGroup} />
          </div>

          {trendRows.length > 1 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="mb-1 text-sm font-semibold text-gray-900">Weekly spend, ad sales and TACOS</h2>
              <p className="mb-3 text-xs text-gray-500">Days after {dayLabel(data.meta.settled_to)} are still settling (Amazon restates recent days), so the last point can move.</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendRows}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="money" tickFormatter={shortMoney} tick={{ fontSize: 12 }} width={70} />
                    <YAxis yAxisId="pct" orientation="right" tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} width={45} />
                    <Tooltip formatter={(value, name) => (String(name).includes('TACOS') ? pct(value) : wholeMoney(value))} />
                    <Legend />
                    <Bar yAxisId="money" dataKey="spend" name="Ad spend" fill="#7C3AED" />
                    <Bar yAxisId="money" dataKey="ad_sales" name="Ad sales" fill="#93C5FD" />
                    <Line yAxisId="pct" type="monotone" dataKey="tacos" name="TACOS" stroke="#111827" strokeWidth={2} dot={false} />
                    {brandsInTrend.map((b, i) => (
                      <Line key={b} yAxisId="pct" type="monotone" dataKey={`tacos_${b}`} name={`TACOS ${b}`} stroke={['#16A34A', '#EA580C', '#0EA5E9', '#DB2777', '#CA8A04', '#64748B'][i]} strokeDasharray="4 3" dot={false} connectNulls />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <h2 className="mr-2 text-sm font-semibold text-gray-900">By SKU</h2>
                {admin && (
                  <>
                    <Button size="sm" variant={verdictFilter === ALL ? 'default' : 'outline'} onClick={() => setVerdictFilter(ALL)} data-testid="ads-verdict-all">All ({data.by_sku.length})</Button>
                    {Object.entries(VERDICTS).filter(([key]) => verdictCounts[key]).map(([key, v]) => (
                      <Button key={key} size="sm" variant={verdictFilter === key ? 'default' : 'outline'} onClick={() => setVerdictFilter(key)} title={v.action} data-testid={`ads-verdict-${key}`}>
                        {v.label} ({verdictCounts[key]})
                      </Button>
                    ))}
                  </>
                )}
              </div>
              <Button size="sm" variant="outline" disabled={!skuRows.length} data-testid="ads-export"
                onClick={() => downloadCsv(skuCsv(skuRows, admin), `ads-by-sku-${data.meta.date_from}-to-${data.meta.date_to}.csv`)}>
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </div>
            {admin && <p className="text-xs text-gray-500">Verdicts use data to {dayLabel(data.meta.settled_to)} and need {data.meta.attribution_window_days}-day attribution plus two full weeks before saying Cut. Hover a verdict for the reason.</p>}
            <SkuTable rows={skuRows} admin={admin} testId="ads-sku-table" />
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------- settings

const FIELDS = [
  { key: 'settle_days', label: 'Days still settling', help: 'Most recent days left out of verdicts while Amazon restates them', min: 0, max: 14 },
  { key: 'min_orders', label: 'Minimum attributed orders', help: 'Fewer than this in the settled window → "Too early"', min: 1, max: 1000 },
  { key: 'scale_headroom_pts', label: 'Scale headroom (points)', help: 'ACOS this far under break-even (with stock) → "Scale"', min: 0, max: 100, step: 0.5 },
  { key: 'hold_floor_pts', label: 'Hold floor (points)', help: 'Headroom below this for two full weeks → "Cut"', min: -100, max: 0, step: 0.5 },
  { key: 'scale_stock_days', label: 'Stock needed to scale (days)', help: 'Days of cover before "Scale" is suggested', min: 1, max: 365 },
  { key: 'pause_stock_days', label: 'Pause below (days of stock)', help: 'Under this → "Pause: stock" and counted as wasted spend', min: 0, max: 365 },
  { key: 'no_sales_days', label: 'No-sales window (days)', help: 'Spend with zero ad sales for this long → wasted spend', min: 1, max: 90 },
  { key: 'tacos_rise_pts', label: 'TACOS rise alert (points)', help: 'Brand TACOS up by this over 4 weeks → alert', min: 0.1, max: 100, step: 0.5 },
  { key: 'ads_gst_pct', label: 'GST on ad invoices (%)', help: 'Amazon Ads bills GST on top of spend', min: 0, max: 100, step: 0.5 },
];

const AdsSettings = ({ country }) => {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    setError(null);
    axios.get(`${API}/settings`, { params: { country } }).then(r => setSettings(r.data)).catch(setError);
  }, [country]);
  useEffect(load, [load]);
  if (error) return <LoadError what="ads settings" error={error} onRetry={load} />;
  if (!settings) return <p className="text-sm text-gray-500">Loading…</p>;
  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(FIELDS.map(f => [f.key, Number(settings[f.key])]));
      const { data } = await axios.put(`${API}/settings`, { ...body, ads_gst_is_cost: !!settings.ads_gst_is_cost }, { params: { country } });
      setSettings(data);
      toast.success('Ads settings saved');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="ads-settings">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900">Verdict rules</h2>
        <p className="mb-3 text-xs text-gray-600">Break-even ACOS = contribution per unit before ads ÷ selling price. These thresholds turn headroom, stock and evidence into Scale / Hold / Cut.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(f => (
            <label key={f.key} className="flex flex-col gap-1 text-xs font-medium text-gray-700">
              {f.label}
              <Input type="number" min={f.min} max={f.max} step={f.step || 1} value={settings[f.key]} onChange={e => set(f.key, e.target.value)} data-testid={`ads-setting-${f.key}`} />
              <span className="font-normal text-gray-500">{f.help}</span>
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!settings.ads_gst_is_cost} onChange={e => set('ads_gst_is_cost', e.target.checked)} data-testid="ads-setting-gst-cost" />
          Count GST on ad spend as a cost (tick only if you do not claim it back as input credit)
        </label>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="ads-settings-save">
        <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </div>
  );
};

export default function AdsPage() {
  const { country } = useCountry();
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  return (
    <div className="space-y-5" data-testid="ads-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Ads</h1>
        <p className="mt-1 text-sm text-gray-600">
          Amazon ad spend against GalaxyIQ&apos;s own sales{admin ? ', cost and stock: which SKUs to scale, hold or cut' : ' and stock'}.
        </p>
      </div>
      {admin ? (
        <Tabs defaultValue="report">
          <TabsList>
            <TabsTrigger value="report" data-testid="ads-tab-report">Report</TabsTrigger>
            <TabsTrigger value="settings" data-testid="ads-tab-settings">Verdict settings</TabsTrigger>
          </TabsList>
          <TabsContent value="report" className="mt-4"><AdsReport country={country} admin /></TabsContent>
          <TabsContent value="settings" className="mt-4"><AdsSettings country={country} /></TabsContent>
        </Tabs>
      ) : <AdsReport country={country} admin={false} />}
    </div>
  );
}
